import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { addDays } from 'date-fns'
import { CallsService, REQUESTABLE_EXTENSIONS_IN_DAYS } from '../calls.service'
import { DATE_TIME_FORMAT, resolveLocalTimeZone } from '../utils'

export type RequestRetentionExtensionDialogData = {
  callId: number
  callName: string
  callDate: Date
  currentSoftDeletionDate: Date
  /** The lengths the organization's caps leave room for; the rest are shown but cannot be picked. */
  availableExtensionsInDays: number[]
}

@Component({
  selector: 'app-request-retention-extension-dialog',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, MatButton, MatIconButton, MatIcon, TranslatePipe, TranslateDirective, DatePipe],
  templateUrl: './request-retention-extension-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './request-retention-extension-dialog.component.scss',
})
export class RequestRetentionExtensionDialogComponent {
  data = inject<RequestRetentionExtensionDialogData>(MAT_DIALOG_DATA)
  private dialogRef = inject(MatDialogRef<RequestRetentionExtensionDialogComponent>)
  private callsService = inject(CallsService)

  requestMutation = this.callsService.requestRetentionExtensionMutation()
  extensionsInDays = REQUESTABLE_EXTENSIONS_IN_DAYS
  dateTimeFormat = DATE_TIME_FORMAT
  localTimeZone = resolveLocalTimeZone()
  callDate = new Date(this.data.callDate)
  currentSoftDeletionDate = new Date(this.data.currentSoftDeletionDate)
  selectedExtensionInDays: number | null = this.data.availableExtensionsInDays.at(-1) ?? null
  isSubmitting = false

  get hasUnavailableExtensions(): boolean {
    return this.extensionsInDays.some((days) => !this.isAvailable(days))
  }

  get resultingSoftDeletionDate(): Date | null {
    return this.selectedExtensionInDays === null ? null : addDays(this.currentSoftDeletionDate, this.selectedExtensionInDays)
  }

  isAvailable(extensionInDays: number): boolean {
    return this.data.availableExtensionsInDays.includes(extensionInDays)
  }

  select(extensionInDays: number) {
    if (this.isAvailable(extensionInDays)) {
      this.selectedExtensionInDays = extensionInDays
    }
  }

  async submit() {
    if (this.selectedExtensionInDays === null) {
      return
    }
    // Held open while the request is in flight, so that closing it cannot read as having called off a
    // request that still arrives.
    this.isSubmitting = true
    this.dialogRef.disableClose = true
    try {
      await this.requestMutation.mutateAsync({ callId: this.data.callId, extensionInDays: this.selectedExtensionInDays })
      this.dialogRef.close(true)
    } catch {
      this.isSubmitting = false
      this.dialogRef.disableClose = false
    }
  }
}
